import {useEffect, useMemo, useState} from 'react';
import {
    GetEmployeeSupplierByItId,
} from '../../../../../reduxToolkit/EmployeeProviders/EmployeeProvidersApi';
import { getColumns, getSections } from '../../Model/Model';
import { useTranslation } from 'react-i18next';
import { USER_TYPE_MODEL } from '../../../../../enums/userTypeEnum';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../../../../../Routes/routes';
/**
 * @file EmployeeFlowSuppliersDetailsPageApp.jsx
 * @description Custom hook for managing the logic behind viewing detailed supplier employee flow data.
 *
 * @Email: azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @JobTitle: Full Stack Developer
 */

/**
 * Hook for managing supplier employee details, including employees, vehicles, and associated documents.
 *
 * @param {Object} props - Props object.
 * @param {function} props.dispatch - Redux dispatch function.
 * @param {Object} props.params - Route parameters object (must include `id`).
 * @param {function} props.setElementDetails - Setter for supplier detail object.
 *
 * @returns {Object} Pagination props for employees and vehicles.
 */
export const EmployeeFlowSuppliersDetailsPageApp = ({
    dispatch,
    params,
    setElementDetails
}) => {
    const { id } = params;
     const { t } = useTranslation();
     const [userId, setUserId] = useState('');
     const [supplierId, setSupplierId] = useState('');
     const [expandedRowId, setExpandedRowId] = useState(null);
     const navigate = useNavigate(); // Navigation handler for routing
     const location = useLocation();


    // On mount: fetch contractor details
    useEffect(() => {
        getSupplierDetail();
    }, []);

    /**
     * Fetches detailed contractor data by ID.
     * Also retrieves work shifts and customized shifts,
     * and triggers document fetch by user ID.
     */
    const getSupplierDetail = () => {
        dispatch(GetEmployeeSupplierByItId(id)).then(
            ({
                payload: {
                    data: { data, success}
                }
             }) => {
                if (success === true) {
                    setElementDetails(data);
                    setUserId(data?.userId);
                    setSupplierId(data?.id);
                }
            }
        );
    };

    const columns = useMemo(
        () => getColumns(t),
        [t]
    );

    const sections = useMemo(
        () => getSections({
            userTypeId: USER_TYPE_MODEL.SUPPLIER_IN_CHARGE,
            supplierId,
            userId
        }),
        [supplierId, userId]
    );

    const handleRowExpand = (row) => {
        if (row?.id !== expandedRowId) {
            setExpandedRowId(row?.id);
        }
    };

    const handleOnBack = () => {
        const state = location.state;
        if (!state) return;

        if (state.backUrl?.includes('/details/')) {
            navigate(state.backUrl, {
                replace: true,
                state: { currentTab: state.currentTab },
            });
            return;
        }

        navigate(routes.suppliers.list, {
            replace: true,
            state: { currentTab: state.currentTab },
        });
    };
    // Return pagination configurations for use in the component
    return {
        columns,
        sections,
        handleRowExpand,
        handleOnBack
    };
};
